import { Box, Typography } from '@mui/material'
import { CheckBox } from '../../../../types/models/Presc'
import { EditableValue } from './EditableValue'
import { ReportToggle } from './ReportToggle'
import { Note } from './Note'
import { colorTokens } from '../../../../shared/styles/theme'
import { LAYOUT } from '../../../../shared/styles/spacing'
import { FieldEditor } from '../fields'

interface Props {
  label: string
  /** 依頼系(分割・医薬品漏れ)のように値を持たない行は省く。 */
  value?: string
  isReported: boolean
  onToggle: () => void
  /** 2 列に置く段はラベルを詰める(全幅 116px / 2 列 88px)。 */
  labelWidth?: number
  /** 直せる項目だけ渡す。undefined なら素の文字列を出す(ADR-0032)。 */
  editor?: FieldEditor
  choices?: CheckBox[]
  /** 編集の初期値(コード値のラベル化前)。省略時は value。 */
  rawValue?: string
  /** 失敗を EditableValue が受けるので Promise を返してよい(拒否 = 保存されなかった)。 */
  onSave?: (value: string) => void | Promise<void>
  validate?: (value: string) => string | null
  hint?: string
  /** 値の下に出す一言。報告が通らなかったことを言うのに使う(toast にしない)。 */
  note?: string
}

/**
 * 項目 1 行。**ラベル / 値 / 報告** の 3 列で、報告は右端の固定 gutter に縦一列で揃える
 * (00-principles/03-subtraction.md §1「行内アイコンは文章長に依存する位置に置かず固定 gutter の
 * 縦一列に整列」・コピー右端常時の先例)。
 *
 * 値は**折り返す**(紙と突き合わせる対象なので省略しない)。値が空でも行は消さない
 * — 読めなかったことが情報で、そこが報告の対象になる。**空でも当たりは残す**
 * (EditableValue が「未記入」/「(なし)」を置く)。
 *
 * 値を直せるかどうかで**報告トグルは変わらない**。「値を直す」と「読み取りが違うと伝える」は
 * 別の行為で、直せない項目でも報告はできる。直せない値は click に理由で応える。
 */
export const FieldRow = ({
  label,
  value,
  isReported,
  onToggle,
  labelWidth = 116,
  editor,
  choices,
  rawValue,
  onSave,
  validate,
  hint,
  note,
}: Props) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1,
      minHeight: LAYOUT.blockRowMinHeight,
      py: 0.25,
    }}
  >
    <Typography
      sx={{
        width: labelWidth,
        flexShrink: 0,
        pt: '4px',
        fontSize: 12,
        color: colorTokens.fg.muted,
      }}
    >
      {label}
    </Typography>
    {/* 値と、その下に出す一言を 1 列にまとめる。EditableValue も自分の Note を
        値の下に出すので、報告の失敗も同じ位置に揃う */}
    <Box
      sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}
    >
      {/* 値を持たない行(分割の依頼・医薬品漏れ)は値の器そのものを置かない */}
      {value !== undefined && (
        <EditableValue
          display={value}
          raw={rawValue ?? value}
          editor={editor}
          choices={choices}
          label={label}
          onSave={onSave}
          validate={validate}
          hint={hint}
        />
      )}
      {note && <Note>{note}</Note>}
    </Box>
    <ReportToggle
      isReported={isReported}
      onToggle={onToggle}
      fieldLabel={label}
    />
  </Box>
)
